import { Injectable, inject, computed } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, debounceTime, distinctUntilChanged, startWith } from 'rxjs/operators';
import { fromEvent } from 'rxjs';

import { getItemsPerPage } from '../../shared/utils/items-per-page';

type LayoutKind = 'mobile' | 'tablet' | 'desktop';

@Injectable({
  providedIn: 'root',
})
export class LayoutService {
  private readonly breakpointObserver = inject(BreakpointObserver);

  /** Alinhado ao SCSS / design tokens do projeto. */
  private readonly breakpointsMap = {
    mobile: ['(max-width: 767.98px)'],
    tablet: ['(min-width: 768px) and (max-width: 1279.98px)'],
    desktop: ['(min-width: 1280px)'],
  };

  readonly isMobile = toSignal(
    this.breakpointObserver
      .observe(this.breakpointsMap.mobile)
      .pipe(map((result) => result.matches)),
    { initialValue: false },
  );

  readonly isTablet = toSignal(
    this.breakpointObserver
      .observe(this.breakpointsMap.tablet)
      .pipe(map((result) => result.matches)),
    { initialValue: false },
  );

  readonly isDesktop = toSignal(
    this.breakpointObserver
      .observe(this.breakpointsMap.desktop)
      .pipe(map((result) => result.matches)),
    { initialValue: true },
  );

  readonly layout = computed<LayoutKind>(() => {
    if (this.isMobile()) {
      return 'mobile';
    }
    if (this.isTablet()) {
      return 'tablet';
    }
    return 'desktop';
  });

  private readonly viewportWidth = toSignal(
    fromEvent(window, 'resize').pipe(
      startWith(null),
      map(() => window.innerWidth),
      debounceTime(200),
      distinctUntilChanged(),
    ),
    { initialValue: window.innerWidth },
  );

  readonly itemsPerPage = computed(() => getItemsPerPage(this.viewportWidth()));
}
